<template>
  <section
    class="flex min-w-0 max-w-full items-center rounded-8 p-2 gap-3 cursor-pointer hover:bg-bg-base-bg3 group transition-all"
    @mouseenter="onHoverShow"
    @mouseover="onTooltipMouseOver"
    @mouseleave="onHoverHide"
    @contextmenu.prevent="showContext($event)"
  >
    <div class="relative flex-none">
      <contact-avatar
        class="flex-none"
        :contact="contact"
        :contact-type="contact.type"
      />
      <template v-if="contact.imLogin">
        <contact-status
          :p-served="contact.imLogin"
          compact-mode
        />
      </template>
    </div>
    <div class="flex min-w-0 grow flex-col gap-y-1 overflow-hidden">
      <p class="text-heading-m-16-22 text-fg-base-fg3 truncate mb-[2px]">
        {{ name }}
      </p>
      <div class="flex min-w-0 items-center gap-x-2 overflow-hidden text-label-r-13-18 text-fg-base-fg22">
        <wui-icon
          :name="iconName"
          :class="iconColor"
          class="flex-none shrink-0"
          small
        />
        <p class="min-w-0 truncate tracking-[0.4px]">
          {{ contact.internalNumber }}
        </p>
      </div>
    </div>

    <div class="relative flex flex-none shrink-0 items-center justify-end w-24 h-11">
      <div
        class="text-label-r-13-18 text-fg-base-fg22 absolute inset-0 grid grid-rows-2 content-center justify-items-end gap-y-2 overflow-hidden transition-opacity"
        :class="{ 'opacity-0 invisible': hover && !callInProgress }"
      >
        <p class="min-w-0 max-w-full truncate whitespace-nowrap">
          {{ date }}
        </p>
        <p class="min-w-0 max-w-full truncate whitespace-nowrap tracking-[0.4px]">
          {{ duration }}
        </p>
      </div>
      <wui-btn
        v-if="webSoftphoneEnabled && hover && !callInProgress"
        class="relative z-10"
        :title="$t('common.makeCall')"
        :aria-label="$t('common.makeCall')"
        variant="brand"
        state="tonal"
        icon
        auto-loading
        :disabled="isCalling"
        :prepend-icon="isCalling ? 'spinner' : 'phoneM'"
        @click.stop="call"
      />
    </div>
    <contact-hover-tooltip
      :contact="contact"
      :visible="isTooltipVisible"
      :mouse-x="mouseX"
      :mouse-y="mouseY"
    />
  </section>
</template>

<script setup lang="ts">
import { IconName, WuiIcon, WuiBtn, useRenderNotifier } from '@wui/common-library'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'


import { ContactHoverTooltip, useContactHoverTooltip } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useContact } from '@/entities/use-contact'
import { callFromAnotherTerminalIfNeeded } from '@/entities/user-terminal'
import { isExpectedCallAsyncRejection } from '@/entities/webrtc'

import { useStatusSubscribe } from '@/shared/composables/im'
import { getMicrophonePermissionErrorMessage } from '@/shared/composables/media-stream'
import { useHover } from '@/shared/composables/use-hover'
import { useWebSoftphoneEnabled } from '@/shared/composables/user-features'
import { ContactStatus } from '@/shared/contact-status'
import { Contact } from '@/shared/types/api'

import { getCallForwardedDisplayLabel, isCallForwardedEvent } from '../lib'
import { CallEventWithStatus } from '../types'

const props = defineProps<{
  contact: Contact
  iconName: IconName
  iconColor: string
  date: string
  duration: string
  callInProgress: boolean
  eventItem: CallEventWithStatus
}>()

const emits = defineEmits<{
  (event: 'show-context', e: MouseEvent | TouchEvent, contact: Contact): void
}>()

const showContext = (event: MouseEvent | TouchEvent) => {
  emits('show-context', event, props.contact)
}

const { hover, show, hide } = useHover()
const { webSoftphoneEnabled } = useWebSoftphoneEnabled()
const {
  mouseX,
  mouseY,
  isTooltipVisible,
  onMouseEnter: onTooltipMouseEnter,
  onMouseOver: onTooltipMouseOver,
  onMouseLeave: onTooltipMouseLeave,
} = useContactHoverTooltip()
const { notifyError } = useRenderNotifier()
const isCalling = ref(false)

const onHoverShow = () => {
  show()
  onTooltipMouseEnter()
}

const onHoverHide = () => {
  hide()
  onTooltipMouseLeave()
}

const name = computed(() => {
  if (isCallForwardedEvent(props.eventItem)) {
    return getCallForwardedDisplayLabel(props.eventItem, props.contact.name)
  }
  return props.contact.name
})

const call = async () => {
  if (isCalling.value) {
    return
  }

  isCalling.value = true
  try {
    if (props.contact?.imLogin && await callFromAnotherTerminalIfNeeded(props.contact?.imLogin)) {
      return
    }

    const { audioCall } = useContact(props.contact)
    await audioCall()
  } catch (e: unknown) {
    if (isExpectedCallAsyncRejection(e)) {
      return
    }
    const errorMessage = getMicrophonePermissionErrorMessage(e)
    notifyError(errorMessage)
  } finally {
    isCalling.value = false
  }
}

const { subscribe, unsubscribe } = useStatusSubscribe()

onMounted(() => {
  if (props.contact.imLogin) {
    subscribe(props.contact.imLogin)
  }
})

onUnmounted(() => {
  if (props.contact.imLogin) {
    unsubscribe(props.contact.imLogin)
  }
})

watch(() => props.contact.imLogin, (newValue, oldValue) => {
  if (newValue) {
    subscribe(newValue)
  } else if (oldValue) {
    unsubscribe(oldValue)
  }
})
</script>
